import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { retryBackoff } from 'backoff-rxjs';
import { BehaviorSubject, map, Observable, of, shareReplay, switchMap, tap } from 'rxjs';
import { environment } from 'src/environments/environment';

import { Bookmark } from '../interfaces/Bookmark';
import { FileMetada } from '../interfaces/FileMetadata';
import { DocumentDataModel } from '../models/document-data.model';
import { ProductionViewService } from './production-view.service';

@Injectable({
  providedIn: 'root',
})
export class CommonService {
  private documentData$?: Observable<DocumentDataModel>;
  private documentDataSubject = new BehaviorSubject<DocumentDataModel | null>(null);

  private readonly retryConfig = { initialInterval: 1000, maxRetries: 3 };

  // local, in-memory caches (for extra performance on top of HTTP caching)
  private bookmarksCache = new Map<string, Bookmark[]>();
  private fileMetadataCache = new Map<string, FileMetada>();

  private productionViewService = inject(ProductionViewService);
  private router = inject(Router);
  private http = inject(HttpClient);

  /** -----------------------------
   *   Project and Document Data
   *  ----------------------------- */

  setProject(project: string): void {
    this.productionViewService.project = project;
    this.clearCache();
  }

  private extractFolderHash(): string {
    const segments = this.router.url.split('/').filter(Boolean);
    return segments.at(-1)!;
  }

  private fetchDocumentData(raw = false): Observable<DocumentDataModel> {
    const folderHash = this.extractFolderHash();
    return this.productionViewService.getDocumentData(folderHash, raw).pipe(
      tap((data) => {
        this.documentDataSubject.next(data);
      })
    );
  }

  readDocumentData(raw = false): Observable<DocumentDataModel> {
    if (!this.documentData$ || raw) {
      this.documentData$ = this.fetchDocumentData(raw).pipe(
        shareReplay({ bufferSize: 1, refCount: true })
      );
    }
    return this.documentData$;
  }

  clearCache(): void {
    this.documentData$ = undefined;
    this.documentDataSubject.next(null);
    this.bookmarksCache.clear();
    this.fileMetadataCache.clear();
  }

  /** -----------------------------
   *   Derived Data Getters
   *  ----------------------------- */

  getFolderHash(): Observable<string> {
    return this.readDocumentData().pipe(map((data) => data.folderHash));
  }

  getDocumentImages(): Observable<string[]> {
    return this.readDocumentData().pipe(
      map((data) =>
        data.imagesList.length > 0
          ? data.imagesList.map((img) => img.imageHash)
          : data.tagsHistory.at(-1)?.at(-1)?.images ?? []
      )
    );
  }

  getDocumentFiles(): Observable<string[]> {
    return this.readDocumentData().pipe(
      map((data) => data.fileList.map((file) => file.fileHash))
    );
  }

  /** -----------------------------
   *   File and Bookmark Endpoints
   *  ----------------------------- */

  getFileMetadata(fileHash: string): Observable<FileMetada> {
    if (this.fileMetadataCache.has(fileHash)) {
      return of(this.fileMetadataCache.get(fileHash)!);
    }

    const url = `${environment.apiUrl}/Files/File?hash=${fileHash}`;
    return this.http.get<FileMetada>(url).pipe(
      tap((meta) => this.fileMetadataCache.set(fileHash, meta)),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  getBookmarks({ useArea = true, rawDocData = false } = {}): Observable<Bookmark[]> {
    return this.readDocumentData(rawDocData).pipe(
      switchMap((data) => {
        const cacheKey = `${data.project}-${useArea ? data.area : 'global'}`;
        const cached = this.bookmarksCache.get(cacheKey);
        if (cached) return of(cached);

        const base = `${environment.portalURL}/api/BookmarksH?project=${data.project}`;
        const url = useArea ? `${base}&area=${data.area}` : base;

        return this.http.get<Bookmark[]>(url).pipe(
          tap((bookmarks) => this.bookmarksCache.set(cacheKey, bookmarks)),
          shareReplay({ bufferSize: 1, refCount: true })
        );
      })
    );
  }

  getRawMailBody(): Observable<string> {
    return this.readDocumentData().pipe(
      switchMap((data) =>
        this.http.get(`${environment.apiUrl}Data/GetDataBinary?hash=${data.mailInfo.rawBody}`, {
          responseType: 'text',
        })
      ),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  /** -----------------------------
   *   Actions (POST requests)
   *  ----------------------------- */

  sendDataEntryToQueueRelay(msg: unknown, retry = true): Observable<any> {
    const url = `${environment.queueRelay}/api/QueueRelay/SendToController`;
    const request = this.http.post(url, msg, { observe: 'response' });
    return retry ? request.pipe(retryBackoff(this.retryConfig)) : request;
  }

  unlockDocument(): Observable<any> {
    return this.getFolderHash().pipe(
      switchMap((folderHash) => {
        const url = `${environment.portalURL}/api/ManualProcess/UnlockEntry?folderHash=${folderHash}`;
        return this.http.post(url, null, { observe: 'response' });
      })
    );
  }
}
